<template>
  <v-navigation-drawer
    v-model="showSearchPanel"
    class="side-panel-search"
    floating
    width="500"
    location="right"
    temporary
    touchless
  >
    <v-container fluid>
      <v-row class="d-flex justify-end">
        <v-btn
          id="side-panel-search--close-button"
          class="float-right ma-2"
          density="comfortable"
          icon="mdi-close"
          variant="text"
          @click="showSearchPanel = false"
        ></v-btn>
      </v-row>
      <v-row>
        <v-tabs v-model="active" fixed-tabs grow bg-color="white" color="primary" class="mx-n1">
          <v-tab id="side-panel-search--content-tab" class="text-grey-darken-1">Content</v-tab>
          <v-tab id="side-panel-search--quicklinks-tab" class="text-grey-darken-1">
            Quicklinks
          </v-tab>
        </v-tabs>
        <v-window v-model="active" class="side-panel-search--tab-item">
          <v-window-item>
            <div class="pl-2 pr-2">
              <v-container fluid class="side-panel-search---content-tab-item">
                <v-row>
                  <v-col cols="12">
                    <v-text-field
                      id="side-panel-search--search-text"
                      v-model="contentSearchText"
                      class="pt-0"
                      label="Search"
                      color="primary"
                      variant="underlined"
                      hide-details
                      single-line
                      append-inner-icon="mdi-magnify"
                      @click:append-inner="getContentSearchResults"
                      @keyup.enter="getContentSearchResults"
                    />

                    <div class="font-weight-bold mt-4">Filter</div>
                    <v-checkbox-btn
                      id="side-panel-search-content--exact-match-checkbox"
                      v-model="searchContentByExactMatchOnly"
                      class="ml-n2 search-by-exact-match"
                      label="Exact Match Only"
                      color="primary"
                    >
                    </v-checkbox-btn>
                    <v-checkbox-btn
                      id="side-panel-search-content--page-title-checkbox"
                      v-model="searchByPageTitleOnly"
                      class="ml-n2 search-by-page-title"
                      label="Search by Page Title only"
                      color="primary"
                    >
                    </v-checkbox-btn>
                    <v-checkbox-btn
                      v-if="isAdmin()"
                      id="side-panel-search-content--invisible-pages-checkbox"
                      v-model="includeInvisiblePages"
                      class="ml-n2 search-by-invisible-pages"
                      label="Include Pages where Visibility is OFF"
                      color="primary"
                    >
                    </v-checkbox-btn>
                    <v-combobox
                      id="side-panel-search-content--search-by-modules-combobox"
                      ref="moduleFilterField"
                      v-model="selectedFilterModules"
                      class="side-panel-search-content--search-by-modules mt-4"
                      variant="underlined"
                      color="primary"
                      :items="activatedCEModules"
                      item-title="displayName"
                      item-value="id"
                      label="Filter by Modules"
                      chips
                      closable-chips
                      clearable
                      multiple
                      :menu-props="{ maxWidth: '460' }"
                      @focus="formatSelectList"
                      @blur="formatChips"
                    >
                      <template #chip="data">
                        <v-chip
                          density="default"
                          size="default"
                          closable
                          @click:close="removeSelectedModule(data.item.value)"
                        >
                          <strong>{{ data.item.title }}&nbsp;</strong>
                        </v-chip>
                      </template>
                      <template #append-item>
                        <div id="side-panel-search--apply-modules-filter-btn-wrapper">
                          <cs-button
                            id="side-panel-search--apply-modules-filter-btn"
                            label="Apply Filters"
                            large
                            primary
                            block
                            :disabled="!selectedFilterModules.length"
                            @click.stop.prevent="applyModuleSearchFilter"
                          />
                        </div>
                      </template>
                    </v-combobox>
                    <div class="d-flex justify-end">
                      <cs-button
                        id="side-panel-search--content-search-btn"
                        label="Search"
                        large
                        primary
                        @click="getContentSearchResults"
                      ></cs-button>
                    </div>
                  </v-col>
                  <v-col
                    v-if="
                      !contentSearchText.trim().length &&
                      !contentSearchKeyword.length &&
                      !contentSearchResults.length
                    "
                    cols="12"
                  >
                    No data available.
                  </v-col>
                  <v-col
                    v-if="
                      contentSearchResultsLoaded &&
                      contentSearchKeyword.length &&
                      !contentSearchResults.length
                    "
                    cols="12"
                  >
                    No search results available, please refine your search.
                  </v-col>
                </v-row>
                <v-row v-if="contentSearchResults.length" class="elastic-search-results pb-2">
                  <v-col cols="12">
                    <div>Showing search results for "{{ contentSearchKeyword }}".</div>
                    <div>Please click on "Search" to refresh results.</div>
                  </v-col>
                  <v-col v-for="(item, index) in visiblePages" :key="index" cols="12" class="pa-2">
                    <v-card
                      :id="`elastic-side-panel-search--content-item-${index}`"
                      :key="index"
                      flat="flat"
                      ripple="ripple"
                      hover="hover"
                      @click="showPage(item.moduleId, item.pagePath)"
                    >
                      <v-card-text>
                        <div class="module-name">
                          <span v-html="item.moduleName"> </span>
                        </div>
                        <span class="page-name" v-html="item.pageTitle"></span><span> -- </span
                        ><span class="page-content" v-html="item.content"></span>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
                <v-row
                  v-if="hasMoreContentResults && page === numberOfPages"
                  class="justify-center pb-2"
                >
                  <cs-button
                    id="elastic-side-panel-search--fetch-more-content-button"
                    label="Load More Results"
                    large
                    primary
                    @click="fetchMoreContentSearchResults"
                  ></cs-button>
                </v-row>
                <v-row v-if="contentSearchResults.length" class="justify-center">
                  <v-pagination
                    id="elastic-side-panel-search--content-pagination"
                    v-model="page"
                    :length="numberOfPages"
                    :total-visible="6"
                    density="comfortable"
                    variant="elevated"
                    active-color="primary"
                  ></v-pagination>
                </v-row>
              </v-container>
            </div>
          </v-window-item>
          <v-window-item>
            <div class="pl-2 pr-2">
              <v-container fluid class="side-panel-search---quicklinks-tab-item">
                <v-row>
                  <v-col cols="12">
                    <v-text-field
                      id="side-panel-search--ql-search-text"
                      v-model="quickLinksSearchText"
                      class="pt-0"
                      label="Search"
                      color="primary"
                      variant="underlined"
                      hide-details
                      single-line
                      append-inner-icon="mdi-magnify"
                      @click:append-inner="getQuicklinksSearchResults"
                      @keyup.enter="getQuicklinksSearchResults"
                    />

                    <div class="font-weight-bold mt-4">Filter</div>
                    <v-checkbox-btn
                      id="side-panel-search-quicklinks--exact-match-checkbox"
                      v-model="searchQuickLinksByExactMatchOnly"
                      class="ml-n2 search-by-exact-match"
                      label="Exact Match Only"
                      color="primary"
                    >
                    </v-checkbox-btn>
                    <div class="d-flex justify-end">
                      <cs-button
                        id="side-panel-search--quicklinks-search-btn"
                        label="Search"
                        large
                        primary
                        @click="getQuicklinksSearchResults"
                      ></cs-button>
                    </div>
                  </v-col>
                  <v-col
                    v-if="
                      !quickLinksSearchText.trim().length &&
                      !quicklinksSearchKeyword.length &&
                      !quicklinksSearchResults.length
                    "
                    cols="12"
                  >
                    No data available.
                  </v-col>
                  <v-col
                    v-if="
                      quicklinksSearchResultsLoaded &&
                      quicklinksSearchKeyword.length &&
                      !quicklinksSearchResults.length
                    "
                    cols="12"
                  >
                    No search results available, please refine your search.
                  </v-col>
                </v-row>
                <v-row
                  v-if="quicklinksSearchResults.length"
                  align="center"
                  justify="center"
                  class="elastic-search-results pb-2"
                >
                  <v-col cols="12">
                    <div>Showing search results for "{{ quicklinksSearchKeyword }}".</div>
                    <div>Please click on "Search" to refresh results.</div>
                  </v-col>
                  <v-col
                    v-for="(item, index) in visiblePages"
                    :key="index"
                    class="flex-grow-0 pa-1"
                  >
                    <v-card
                      :id="`elastic-side-panel-search--quicklinks-item-${index}`"
                      :key="item.id"
                      class="qlcard"
                      width="120px"
                      height="140px"
                      ripple="ripple"
                      hover="hover"
                    >
                      <app-link
                        :to="getQuickLinkClickableUrl(item, 'to')"
                        :is-external-link="
                          getQuickLinkItemType(item) === 'External' ? true : undefined
                        "
                        @click="quickLinkClickHandler(item)"
                      >
                        <img
                          alt="Icon"
                          class="iconPadding"
                          :class="{ 'hidden-quicklink': item.hidden }"
                          width="120px"
                          :src="item.icon"
                        />
                        <v-card-text class="quicklink-title" width="90px">
                          <span v-html="item.title"></span>
                        </v-card-text>
                      </app-link>
                    </v-card>
                  </v-col>
                </v-row>
                <v-row
                  v-if="hasMoreQuicklinksResults && page === numberOfPages"
                  class="justify-center pb-2"
                >
                  <cs-button
                    id="elastic-side-panel-search--fetch-more-quicklinks-button"
                    label="Load More Results"
                    large
                    primary
                    @click="fetchMoreQuicklinksSearchResults"
                  ></cs-button>
                </v-row>
                <v-row v-if="quicklinksSearchResults.length" class="justify-center">
                  <v-pagination
                    id="elastic-side-panel-search--quicklinks-pagination"
                    v-model="qlPage"
                    :length="numberOfPages"
                    :total-visible="6"
                    density="comfortable"
                    variant="elevated"
                    active-color="primary"
                  ></v-pagination>
                </v-row>
              </v-container>
            </div>
          </v-window-item>
        </v-window>
      </v-row>
    </v-container>
  </v-navigation-drawer>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { CSBase } from '@complispace/cs-design-system';
import * as types from '@/store/mutationTypes';
import { blobs, content as contentAPI } from '@/api';
import { UPDATE_CMD } from '@/constants';
import routerUtils from '@/lib/routerUtils';
import AppLink from '@/components/core/AppLink';
import sessionManager from '../../store/modules/session/sessionManager';

export default {
  name: 'SidePanelSearch',

  components: {
    'app-link': AppLink
  },

  extends: CSBase,

  emits: ['updateNav'],

  data() {
    return {
      contentSearchKeyword: '',
      quicklinksSearchKeyword: '',
      page: 1,
      qlPage: 1,
      contentSearchResults: [],
      quicklinksSearchResults: [],
      hasMoreContentResults: false,
      hasMoreQuicklinksResults: false,
      contentPagesFetched: 0,
      quicklinksPagesFetched: 0,
      contentSearchResultsLoaded: true,
      quicklinksSearchResultsLoaded: true,
      active: null,
      cardDefaultWidth: '120px',
      openFileType: ['.pdf', '.gif', '.jp2', '.jpe', '.jpeg', '.jpg', '.png', '.bmp'],
      searchByPageTitleOnly: false,
      searchContentByExactMatchOnly: false,
      searchQuickLinksByExactMatchOnly: false,
      includeInvisiblePages: false,
      selectedFilterModules: []
    };
  },

  watch: {
    showSearchPanel(value) {
      // Every time the search panel is closed, any operation may be done that changes the Content/Quicklinks results.
      // Hence, we re-fetch the search results on opening the search panel again.
      if (value) {
        this.contentSearchKeyword = '';
        this.getContentSearchResults();
        this.quicklinksSearchKeyword = '';
        this.getQuicklinksSearchResults();
      }
    },

    searchByPageTitleOnly() {
      if (
        this.contentSearchResults.length &&
        this.contentSearchText.trim() === this.contentSearchKeyword
      ) {
        this.getContentSearchResults();
      }
    },

    searchContentByExactMatchOnly() {
      if (
        this.contentSearchResults.length &&
        this.contentSearchText.trim() === this.contentSearchKeyword
      ) {
        this.getContentSearchResults();
      }
    },
    searchQuickLinksByExactMatchOnly() {
      if (
        this.quicklinksSearchResults.length &&
        this.quickLinksSearchText.trim() === this.quicklinksSearchKeyword
      ) {
        this.getContentSearchResults();
      }
    },
    includeInvisiblePages() {
      if (
        this.contentSearchResults.length &&
        this.contentSearchText.trim() === this.contentSearchKeyword
      ) {
        this.getContentSearchResults();
      }
    }
  },
  computed: {
    ...mapState({
      subdomain: (state) => state.session.subdomain,
      client: (state) => state.client
    }),

    ...mapGetters({
      activatedCEModules: 'activatedCEModules/items',
      contextItems: 'clientContext/items',
      role: 'authorisation/role',
      isLoggedIn: 'authorisation/isLoggedIn'
    }),

    showSearchPanel: {
      get() {
        return this.$store.state.appearance.showSearchPanel;
      },

      set(value) {
        this.$store.commit(types.APPEARANCE_SET_SEARCH_PANEL, value);
      }
    },

    contentSearchText: {
      get() {
        return this.$store.state.app.contentSearchText;
      },
      set(value) {
        this.$store.commit(types.APP_SET_CONTENT_SEARCH_TEXT, value);
      }
    },

    quickLinksSearchText: {
      get() {
        return this.$store.state.app.quickLinksSearchText;
      },
      set(value) {
        this.$store.commit(types.APP_SET_QUICK_LINK_SEARCH_TEXT, value);
      }
    },

    indexName() {
      return 'USER_MODULES';
    },

    qlIndexName() {
      return 'QUICK_LINKS';
    },

    numberOfPages() {
      let pages = 0;

      switch (this.active) {
        case 0:
          pages = Math.ceil(this.contentSearchResults.length / 10);
          break;
        case 1:
          pages = Math.ceil(this.quicklinksSearchResults.length / 21);

          break;
        default:
          break;
      }

      return pages;
    },

    visiblePages() {
      let pages = [];

      switch (this.active) {
        case 0:
          pages = this.contentSearchResults.slice((this.page - 1) * 10, this.page * 10);
          break;
        case 1:
          pages = this.quicklinksSearchResults.slice((this.qlPage - 1) * 21, this.qlPage * 21);

          break;
        default:
          break;
      }

      return pages;
    }
  },

  async mounted() {
    if (this.isLoggedIn) {
      try {
        // Set x-site-uri in case it hasn't been set before the backend service call.
        sessionManager.setAdditionalHeaders();
      } catch (error) {
        console.log(error);
      }
    }
  },

  methods: {
    isAdmin() {
      const roleId = parseInt(this.role, 10);
      if (roleId === 4 || roleId === 3 || roleId === 2) {
        return true;
      }
      return false;
    },

    removeSelectedModule(item) {
      this.selectedFilterModules = this.selectedFilterModules.filter(
        (module) => module.id !== item
      );
    },

    formatSelectList() {
      this.$nextTick(() => {
        const vMenuContentElement = document.querySelector(
          '.v-menu__content.v-menu__content--fixed.v-autocomplete__content'
        );

        if (vMenuContentElement) {
          const { minWidth } = vMenuContentElement.style;

          if (minWidth) {
            vMenuContentElement.style.width = minWidth;
          }

          const vListItemTitleElements = document.querySelectorAll(
            '.v-menu__content.v-menu__content--fixed.v-autocomplete__content .v-select-list .v-list-item .v-list-item__title'
          );

          vListItemTitleElements.forEach((vListItemTitleElement) => {
            vListItemTitleElement.setAttribute('title', vListItemTitleElement.innerText);
          });
        }
      });
    },

    formatChips() {
      this.$nextTick(() => {
        const vChipContentElements = document.querySelectorAll(
          '.v-input.side-panel-search-content--search-by-modules .v-input__control .v-select__selections .v-chip .v-chip__content strong'
        );

        vChipContentElements.forEach((vChipContentElement) => {
          vChipContentElement.setAttribute('title', vChipContentElement.innerText);
        });
      });
    },

    applyModuleSearchFilter() {
      this.$refs.moduleFilterField.blur();

      if (
        this.contentSearchResults.length &&
        this.contentSearchText.trim() === this.contentSearchKeyword
      ) {
        this.getContentSearchResults();
      }
    },

    async getContentSearchResults() {
      if (this.contentSearchResultsLoaded) {
        this.contentSearchKeyword = this.contentSearchText.trim();

        if (!this.contentSearchText.trim().length) {
          this.contentSearchResults = [];
          this.hasMoreContentResults = false;
          this.contentPagesFetched = 0;
        } else {
          try {
            this.setLoading(true);
            this.contentSearchResultsLoaded = false;

            const searchFields = [];

            if (this.searchByPageTitleOnly) {
              searchFields.push('title');
            }

            const moduleIds = this.selectedFilterModules.map((module) => module.id);

            const contentSearchPayload = {
              fromPage: 0,
              searchText: this.contentSearchKeyword,
              includeInvisiblePages: this.includeInvisiblePages && this.isAdmin(),
              searchByExactMatchOnly: this.searchContentByExactMatchOnly,
              searchFields: searchFields.length ? searchFields : undefined,
              moduleIds: moduleIds.length ? moduleIds : undefined
            };

            const contentResponse =
              await contentAPI.searchElasticSearchContent(contentSearchPayload);

            if (contentResponse && contentResponse.status === 200 && contentResponse.data) {
              this.contentSearchResults = contentResponse.data.searchResults;
              this.hasMoreContentResults = contentResponse.data.hasMoreSnippets;
              this.contentPagesFetched = contentResponse.data.pagesFetched;
            }
          } catch (e) {
            this.showErrorResponseAlert(e);
          } finally {
            this.setLoading(false);

            // In the scenario when the user types a long search query, we don't send all the intermediate requests to the backend.
            // Once the user has stopped typing and if the final request is not sent to the backend, search again with the latest search text.
            if (this.contentSearchKeyword !== this.contentSearchText.trim()) {
              this.getContentSearchResults();
            }
          }
        }

        this.contentSearchResultsLoaded = true;
      }

      this.page = 1;
    },

    async getQuicklinksSearchResults() {
      if (this.quicklinksSearchResultsLoaded) {
        this.quicklinksSearchKeyword = this.quickLinksSearchText.trim();

        if (!this.quickLinksSearchText.trim().length) {
          this.quicklinksSearchResults = [];
          this.hasMoreQuicklinksResults = false;
          this.quicklinksPagesFetched = 0;
        } else {
          try {
            this.setLoading(true);
            this.quicklinksSearchResultsLoaded = false;

            const quicklinksSearchPayload = {
              fromPage: 0,
              searchText: this.quicklinksSearchKeyword,
              includeHiddenLink: this.isAdmin(),
              searchByExactMatchOnly: this.searchQuickLinksByExactMatchOnly
            };

            const quicklinksResponse =
              await contentAPI.searchElasticSearchQuicklinks(quicklinksSearchPayload);

            if (
              quicklinksResponse &&
              quicklinksResponse.status === 200 &&
              quicklinksResponse.data
            ) {
              this.quicklinksSearchResults = quicklinksResponse.data.searchResults;
              this.hasMoreQuicklinksResults = quicklinksResponse.data.hasMoreSnippets;
              this.quicklinksPagesFetched = quicklinksResponse.data.pagesFetched;
            }
          } catch (e) {
            this.showErrorResponseAlert(e);
          } finally {
            this.setLoading(false);
            this.quicklinksSearchResultsLoaded = true;

            // In the scenario when the user types a long search query, we don't send all the intermediate requests to the backend.
            // Once the user has stopped typing and if the final request is not sent to the backend, search again with the latest search text.
            if (this.quicklinksSearchKeyword !== this.quickLinksSearchText.trim()) {
              this.getQuicklinksSearchResults();
            }
          }
        }
      }

      this.qlPage = 1;
    },

    async fetchMoreContentSearchResults() {
      this.setLoading(true);
      this.contentSearchResultsLoaded = false;

      try {
        const searchPayload = {
          fromPage: this.contentPagesFetched,
          searchText: this.contentSearchKeyword,
          includeInvisiblePages: this.includeInvisiblePages,
          searchByExactMatchOnly: this.searchContentByExactMatchOnly
        };

        const response = await contentAPI.searchElasticSearchContent(searchPayload);

        if (response && response.status === 200 && response.data) {
          this.contentSearchResults = [
            ...this.contentSearchResults,
            ...response.data.searchResults
          ];
          this.hasMoreContentResults = response.data.hasMoreSnippets;
          this.contentPagesFetched = response.data.pagesFetched;
        }
      } catch (e) {
        this.showErrorResponseAlert(e);
      } finally {
        this.setLoading(false);
        this.contentSearchResultsLoaded = true;
      }
    },

    async fetchMoreQuicklinksSearchResults() {
      this.setLoading(true);
      this.quicklinksSearchResultsLoaded = false;

      try {
        const searchPayload = {
          fromPage: this.quicklinksPagesFetched,
          searchText: this.quicklinksSearchKeyword,
          includeHiddenLink: this.isAdmin(),
          searchByExactMatchOnly: this.searchQuickLinksByExactMatchOnly
        };

        const response = await contentAPI.searchElasticSearchQuicklinks(searchPayload);

        if (response && response.status === 200 && response.data) {
          this.quicklinksSearchResults = [
            ...this.quicklinksSearchResults,
            ...response.data.searchResults
          ];
          this.hasMoreQuicklinksResults = response.data.hasMoreSnippets;
          this.quicklinksPagesFetched = response.data.pagesFetched;
        }
      } catch (e) {
        this.showErrorResponseAlert(e);
      } finally {
        this.setLoading(false);
        this.quicklinksSearchResultsLoaded = true;
      }
    },

    // Replace context keys with their value
    replaceContextKey(content) {
      let replacedContent = content;
      let contextKeyStr;
      try {
        this.contextItems.forEach((contextKey) => {
          contextKeyStr = contextKey.key;
          if (contextKeyStr) {
            const regexSpecialChar = '[&\\/\\\\#,+()$~%.\'":*?<>{}]';
            const regexPatternSpecial = new RegExp(regexSpecialChar, 'g');

            contextKeyStr = contextKeyStr.replace(regexPatternSpecial, '');

            const regexContextKey = `(\\{{\\s*${contextKeyStr}\\s*}})`;
            const regexPatternKey = new RegExp(regexContextKey, 'ig');

            if (contextKey.value) {
              replacedContent = replacedContent.replace(regexPatternKey, contextKey.value);
            } else if (contextKey.placeholder) {
              replacedContent = replacedContent.replace(regexPatternKey, contextKey.placeholder);
            } else {
              replacedContent = replacedContent.replace(
                regexPatternKey,
                `Missing value for context key ${contextKey.key.toLowerCase()}`
              );
            }
          }
        });
      } catch (e) {
        console.log(e);
        // Try rendering
        return e;
      }
      // Remove If Else EndIf conditions. Matching with {%, %} and removing the content

      // eslint-disable-next-line prefer-regex-literals
      const regexPattern = new RegExp('(\\{%.*?\\%})', 'g');
      replacedContent = replacedContent.replace(regexPattern, '');
      return replacedContent;
    },

    // Transform module content by replacing context key with their actual value or placeholder.
    transformContent(items) {
      return items.map((item) => {
        const transformedItem = item;
        // eslint-disable-next-line no-underscore-dangle
        transformedItem._snippetResult.transformedcontent = {
          // eslint-disable-next-line no-underscore-dangle
          value: this.replaceContextKey(item._snippetResult.content.value)
        };
        // eslint-disable-next-line no-underscore-dangle
        transformedItem._highlightResult.transformedtitle = {
          // eslint-disable-next-line no-underscore-dangle
          value: this.replaceContextKey(item._highlightResult.title.value)
        };
        return transformedItem;
      });
    },

    showPage(moduleId, pagePath) {
      // Forcing a close on seach navigation drawer is required to
      // close it the drawer if the user clicks on the search result
      // and the current page are the same.
      this.showSearchPanel = false;
      this.$emit('updateNav', {
        moduleId,
        values: {
          cmd: UPDATE_CMD.SEARCH,
          values: {
            path: pagePath
          }
        }
      });
    },

    showFile(blob, fileName) {
      // Source: https://blog.jayway.com/2017/07/13/open-pdf-downloaded-api-javascript/
      // IE doesn't allow using a blob object directly as link href
      // instead it is necessary to use msSaveOrOpenBlob
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(blob, fileName);
        return;
      }
      // For other browsers:
      // Create a link pointing to the ObjectURL containing the blob.
      const data = window.URL.createObjectURL(blob);
      const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
      if (this.openFileType.indexOf(fileExtension) !== -1) {
        const win = window.open('', '_blank');
        win.location.href = data; // eslint-disable-line no-param-reassign
      } else {
        const link = document.createElement('a');
        link.href = data;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          // For Firefox it is necessary to delay revoking the ObjectURL
          document.body.removeChild(link);
          window.URL.revokeObjectURL(data);
        }, 100);
      }
    },
    getQuickLinkItemType(item) {
      const stripPort = process.env.QUICK_LINKS_API_URL.replace(/:\d{1,4}/, '');
      const isFileUrl = item.url.includes(stripPort);
      if (isFileUrl) {
        return 'File';
      }
      const { isInternalLink } = routerUtils.analyzeUrlForNavigation(item.url);
      return isInternalLink ? 'Internal' : 'External';
    },
    getQuickLinkClickableUrl(item, type) {
      const urlType = this.getQuickLinkItemType(item);
      if (urlType === 'Internal' && type === 'to') {
        return routerUtils.getRelativeUrl(item.url);
      }
      return item.url;
    },
    async quickLinkClickHandler(item) {
      if (this.getQuickLinkItemType(item) === 'File') {
        try {
          this.setLoading(true);
          let id = item.url.split('/');
          id = id[id.length - 1];

          const response = await blobs.getLinks(id, this.subdomain);

          if (response && response.status === 200 && response.data) {
            const contentType = response.headers['content-type'];
            let fileName = response.headers['content-disposition'];
            fileName = fileName.substring(fileName.indexOf('=') + 1);
            fileName = fileName.substring(1, fileName.length - 1);
            const blob = new Blob([response.data], { type: contentType });
            this.showFile(blob, fileName);
          }
        } catch (err) {
          console.error(err);
        } finally {
          this.setLoading(false);
        }
      }
    }
  }
};
</script>

<style scoped>
.side-panel-search {
  z-index: 50;
  background-color: #eff3f6 !important;
  /* Matches .hx-app-header-side-nav positioning from cs-design-system */
  top: 126px !important;
  height: calc(100% - 126px) !important;
}

.side-panel-search--tab-item {
  background-color: #eff3f6 !important;
  width: 100% !important;
}

.elastic-search-results :deep(.v-card__text .page-name),
.customHighlight {
  font-size: 16px;
  color: rgba(0, 0, 0, 0.74);
  font-style: normal;
}

.customHighlightHighlighted {
  font-size: 16px;
  font-style: normal;
  background-color: rgba(0, 0, 0, 0);
  background-image: none;
  background-origin: padding-box;
  border-bottom-color: rgb(84, 104, 255);
  border-bottom-style: solid;
  border-bottom-width: 1px;
  border-image-outset: 0px;
  border-image-repeat: stretch;
  border-image-slice: 100%;
  border-image-source: none;
  border-image-width: 1;
  border-left-color: rgb(84, 104, 255);
  border-left-style: solid;
  border-left-width: 0px;
  border-right-color: rgb(84, 104, 255);
  border-right-style: solid;
  border-right-width: 0px;
  border-top-color: rgb(84, 104, 255);
  border-top-style: solid;
  border-top-width: 0px;
  box-sizing: border-box;
  color: rgba(33, 36, 61, 0.5);
}

.v-card {
  overflow: hidden;
}

.elastic-search-results :deep(.v-card__text .module-name),
.customSnippet {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.54);
}

.customSnippetHighlighted {
  font-size: 14px;
  font-style: normal;
  background-color: rgba(0, 0, 0, 0);
  background-image: none;
  background-origin: padding-box;
  border-bottom-color: rgb(84, 104, 255);
  border-bottom-style: solid;
  border-bottom-width: 1px;
  border-image-outset: 0px;
  border-image-repeat: stretch;
  border-image-slice: 100%;
  border-image-source: none;
  border-image-width: 1;
  border-left-color: rgb(84, 104, 255);
  border-left-style: solid;
  border-left-width: 0px;
  border-right-color: rgb(84, 104, 255);
  border-right-style: solid;
  border-right-width: 0px;
  border-top-color: rgb(84, 104, 255);
  border-top-style: solid;
  border-top-width: 0px;
  box-sizing: border-box;
  color: rgba(33, 36, 61, 0.5);
}

.top {
  position: fixed;
  top: 20px;
  left: 0;
  z-index: 999;
  width: 100%;
  height: 23px;
}

.middle {
  min-height: calc(100vh - 70px);
  overflow: scroll;
}

.bottom {
  position: fixed;
  bottom: 30px;
  left: 0;
  z-index: 999;
  width: 100%;
  height: 23px;
}

@media (max-width: 768px) {
  .v-navigation-drawer--absolute {
    position: fixed;
  }
}

.qlcard {
  max-width: 120px;
}

.iconPadding {
  padding: 7%;
  text-align: center;
  margin: 0 auto;
  max-width: 180px;
  min-width: 80px;
}

.linksTitle,
.quicklink-title {
  color: #696969;
  font-weight: bold;
  height: 28px;
  padding: 5px 0;
  text-align: center;
  line-height: 14px !important;
  font-size: 12px;
}

.hidden-quicklink {
  filter: grayscale(1);
}

.elastic-search-results :deep(.v-card-text .module-name em),
.elastic-search-results :deep(.v-card-text .page-name em),
.elastic-search-results :deep(.v-card-text .page-content em),
.elastic-search-results :deep(.v-card-text.quicklink-title em) {
  background-color: #ffc168 !important;
}

.side-panel-search :deep(.side-panel-search-content--search-by-modules .v-select__selections) {
  max-height: 80px;
  overflow-y: auto;
}

#side-panel-search--apply-modules-filter-btn-wrapper {
  padding: 0 0.5rem 0 0.5rem;
  position: sticky;
  bottom: 0.5rem;
}

.v-menu__content.v-menu__content--fixed.menuable__content__active.v-autocomplete__content
  .v-select-list {
  display: flex !important;
  flex-direction: column !important;
}

.search-by-exact-match :deep(.v-messages),
.search-by-page-title :deep(.v-messages),
.v-input.side-panel-search-content--search-by-modules :deep(.v-text-field__details) {
  display: none;
}

.side-panel-search---content-tab-item :deep(.v-select__selections) {
  overflow: auto;
  width: 100px;
}

.side-panel-search---content-tab-item :deep(.v-select__selections .v-chip__content strong) {
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
